import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useChartPositionContext, ChartPosition } from '../contexts/ChartPositionContext';
import { usePositionContext } from '../contexts/PositionContext';
import { usePositionDrag } from '../hooks/usePositionDrag';

interface DraggablePositionOverlayProps {
  position: ChartPosition;
  index: number;
  currentPrice: number;
  chartRef: React.RefObject<any>;
  overlayRef: React.RefObject<HTMLDivElement>;
}

const DraggablePositionOverlay: React.FC<DraggablePositionOverlayProps> = ({
  position,
  index,
  currentPrice,
  chartRef,
  overlayRef,
}) => {
  const { theme } = useTheme();
  const { isDragEnabled, closeChartPosition, updateChartPosition, setSLLevel, setTPLevel } = useChartPositionContext();
  const { closingPositions } = usePositionContext();
  const {
    dragState,
    isConfirming,
    pendingAction,
    handleMouseDown,
    handleConfirm: originalHandleConfirm,
    handleCancel,
    canDragPosition,
    canDragSLTP,
    getDragConstraints,
  } = usePositionDrag();

  const [confirmingPositionId, setConfirmingPositionId] = useState<string | null>(null);
  
  // Drag flags to track if dragging has been initiated for the first time
  const [isDragInitiated, setIsDragInitiated] = useState<boolean>(false); // For limit/stop orders
  const [isDragInitiatedTP, setIsDragInitiatedTP] = useState<boolean>(false); // For TP
  const [isDragInitiatedSL, setIsDragInitiatedSL] = useState<boolean>(false); // For SL
  
  // Separate state for each element type's original and confirmed positions
  const [positionOriginalData, setPositionOriginalData] = useState<any>(null);
  const [slOriginalData, setSLOriginalData] = useState<any>(null);
  const [tpOriginalData, setTPOriginalData] = useState<any>(null);
  
  // Separate confirmed positions for each element type
  const [positionConfirmedPos, setPositionConfirmedPos] = useState<{y: number, startY: number, deltaY: number} | null>(null);
  const [slConfirmedPos, setSLConfirmedPos] = useState<{y: number, startY: number, deltaY: number} | null>(null);
  const [tpConfirmedPos, setTPConfirmedPos] = useState<{y: number, startY: number, deltaY: number} | null>(null);
  
  
  const positionRef = useRef<HTMLDivElement>(null);
  const slRef = useRef<HTMLButtonElement>(null);
  const tpRef = useRef<HTMLButtonElement>(null);

  const isBuy = position.type === 'Buy';
  const isProfit = position.pnl.startsWith('+');
  const isConfirmingClose = confirmingPositionId === position.id;
  const isConfirmingDrag = isConfirming && pendingAction && pendingAction.positionId === position.id;

  // Check if this position can be dragged
  const canDragWholePosition = canDragPosition(position);
  const canDragSL = canDragSLTP(position);
  const canDragTP = canDragSLTP(position);

  // Get drag constraints for SL/TP
  const slConstraints = getDragConstraints(position, 'sl');
  const tpConstraints = getDragConstraints(position, 'tp');

  // Convert price to Y position on chart
  const priceToY = useCallback((price: number) => {
    if (!chartRef.current || !overlayRef.current) return 0;
    
    try {
      const chart = chartRef.current;
      const overlay = overlayRef.current;
      
      // Check if chart is properly initialized
      if (!chart.priceScale || typeof chart.priceScale !== 'function') {
        return 0;
      }
      
      const chartRect = overlay.getBoundingClientRect();
      const chartHeight = chartRect.height;
      
      if (chartHeight <= 0) {
        return 0;
      }
      
      const priceScale = chart.priceScale('right');
      
      // Check if price scale is available
      if (!priceScale || typeof priceScale.getVisibleRange !== 'function') {
        return chartHeight / 2; // Fallback to middle of chart
      }
      
      const priceRange = priceScale.getVisibleRange();
      
      if (!priceRange) {
        return chartHeight / 2; // Fallback to middle of chart
      }

      const minPrice = priceRange.minValue;
      const maxPrice = priceRange.maxValue;
      
      // Check if price range values are valid
      if (minPrice === undefined || maxPrice === undefined || minPrice === null || maxPrice === null) {
        return chartHeight / 2; // Fallback to middle of chart
      }
      
      const priceRangeSize = maxPrice - minPrice;
      
      if (priceRangeSize <= 0) {
        return chartHeight / 2; // Fallback to middle of chart
      }
      
      const priceRatio = (maxPrice - price) / priceRangeSize;
      const yPosition = priceRatio * chartHeight;
      
      // Debug logging (only in development)
      if (process.env.NODE_ENV === 'development') {
        // console.log('PriceToY Debug:', {
        //   price,
        //   minPrice,
        //   maxPrice,
        //   priceRangeSize,
        //   priceRatio,
        //   chartHeight,
        //   yPosition
        // });
      }
      
      return yPosition;
    } catch (error) {
      console.warn('Error converting price to Y position:', error);
      return 0;
    }
  }, [chartRef, overlayRef]);

  // Convert Y position to price on chart
  const yToPrice = useCallback((y: number) => {
    if (!chartRef.current || !overlayRef.current) return 0;
    
    try {
      const chart = chartRef.current;
      const overlay = overlayRef.current;
      
      const chartRect = overlay.getBoundingClientRect();
      const chartHeight = chartRect.height;
      
      const priceScale = chart.priceScale('right');
      const priceRange = priceScale.getVisibleRange();
      
      if (!priceRange) return 0;

      const minPrice = priceRange.minValue;
      const maxPrice = priceRange.maxValue;
      const priceRangeSize = maxPrice - minPrice;
      
      const priceRatio = (chartHeight - y) / chartHeight;
      return minPrice + (priceRatio * priceRangeSize);
    } catch (error) {
      console.warn('Error converting Y position to price:', error);
      return 0;
    }
  }, [chartRef, overlayRef]);

  // Calculate price from Y position
  const calculatePriceFromY = useCallback((y: number) => {
    return yToPrice(y);
  }, [yToPrice]);

  // Custom handleConfirm that calculates price from Y position
  const handleConfirm = useCallback(() => {
    if (pendingAction) {
      // If no price from drop zone, calculate it from the Y position
      let finalPrice = pendingAction.newPrice;
      if (!finalPrice && pendingAction.newPosition) {
        // Convert Y position to price
        const chartRect = overlayRef.current?.getBoundingClientRect();
        if (chartRect) {
          const relativeY = pendingAction.newPosition.y - chartRect.top;
          finalPrice = calculatePriceFromY(relativeY);
        }
      }
      
      // Store the final price for later use
      const priceToUpdate = finalPrice;
      const actionType = pendingAction.type;
      const positionId = pendingAction.positionId;
      
      // Call the original confirm function first
      originalHandleConfirm();
      
      // Update the position after a short delay to ensure confirmed position is set
      setTimeout(() => {
        switch (actionType) {
          case 'move_position':
            // For pending orders, update the order price
            if (priceToUpdate) {
              updateChartPosition(positionId, { entry: priceToUpdate.toString() });
            }
            break;
          case 'update_sl':
            if (priceToUpdate) {
              setSLLevel(positionId, priceToUpdate);
            }
            break;
          case 'update_tp':
            if (priceToUpdate) {
              setTPLevel(positionId, priceToUpdate);
            }
            break;
        }
      }, 0);
    }
  }, [pendingAction, originalHandleConfirm, calculatePriceFromY, overlayRef, updateChartPosition, setSLLevel, setTPLevel]);

  // Individual drag handlers for each wrap
  const handlePositionMouseDown = useCallback((e: React.MouseEvent) => {
    // Only allow dragging for ordered positions (limit/stop orders), not executed positions
    if (!canDragWholePosition || !position.isPendingOrder) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Set drag flag to true when first drag operation starts
    if (!isDragInitiated) {
      setIsDragInitiated(true);
    }
    
    // If we're already confirming a drag for this position, cancel the previous confirmation first
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id) {
      handleCancel();
    }
    
    const originalData = {
      entry: position.entry,
      slLevel: position.slLevel,
      tpLevel: position.tpLevel,
      confirmedPosition: positionConfirmedPos // Store current confirmed position for position
    };
    
    // Store original position for potential cancel
    setPositionOriginalData(originalData);
    
    // Use the confirmation system directly
    handleMouseDown(e, position.id, 'position', originalData);
  }, [canDragWholePosition, position, isConfirmingDrag, pendingAction, handleCancel, handleMouseDown, positionConfirmedPos, isDragInitiated]);

  // Handle SL drag start
  const handleSLMouseDown = useCallback((e: React.MouseEvent) => {
    // Allow SL drag for both executed and ordered positions
    if (!canDragSL) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Set SL drag flag to true when first drag operation starts
    if (!isDragInitiatedSL) {
      setIsDragInitiatedSL(true);
    }
    
    // If we're already confirming a drag for this position, cancel the previous confirmation first
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id) {
      handleCancel();
    }
    
    // Calculate the current position of the SL element (including any confirmed offset)
    const currentY = e.clientY;
    const confirmedOffset = slConfirmedPos ? slConfirmedPos.deltaY || 0 : 0;
    const adjustedStartY = currentY - confirmedOffset;
    
    const originalData = { 
      slLevel: position.slLevel,
      confirmedPosition: slConfirmedPos, // Store current confirmed position for SL
      adjustedStartY: adjustedStartY // Store the adjusted start Y position
    };
    
    // Store original position for potential cancel
    setSLOriginalData(originalData);
    
    // Use the confirmation system directly
    handleMouseDown(e, position.id, 'sl', originalData);
  }, [canDragSL, position, isConfirmingDrag, pendingAction, handleCancel, handleMouseDown, slConfirmedPos, isDragInitiatedSL]);

  // Handle TP drag start
  const handleTPMouseDown = useCallback((e: React.MouseEvent) => {
    // Allow TP drag for both executed and ordered positions
    if (!canDragTP) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Set TP drag flag to true when first drag operation starts
    if (!isDragInitiatedTP) {
      setIsDragInitiatedTP(true);
    }
    
    // If we're already confirming a drag for this position, cancel the previous confirmation first
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id) {
      handleCancel();
    }
    
    // Calculate the current position of the TP element (including any confirmed offset)
    const currentY = e.clientY;
    const confirmedOffset = tpConfirmedPos ? tpConfirmedPos.deltaY || 0 : 0;
    const adjustedStartY = currentY - confirmedOffset;
    
    const originalData = { 
      tpLevel: position.tpLevel,
      confirmedPosition: tpConfirmedPos, // Store current confirmed position for TP
      adjustedStartY: adjustedStartY // Store the adjusted start Y position
    };
    
    // Store original position for potential cancel
    setTPOriginalData(originalData);
    
    // Use the confirmation system directly
    handleMouseDown(e, position.id, 'tp', originalData);
  }, [canDragTP, position, isConfirmingDrag, pendingAction, handleCancel, handleMouseDown, tpConfirmedPos, isDragInitiatedTP]);

  // Handle close button click
  const handleCloseClick = useCallback(() => {
    setConfirmingPositionId(position.id);
  }, [position.id]);

  // Check if this position is being closed
  const isClosing = closingPositions.has(position.id);

  // Handle confirm close
  const handleConfirmClose = useCallback(() => {
    closeChartPosition(position.id);
    setConfirmingPositionId(null);
  }, [closeChartPosition, position.id]);

  // Handle cancel close
  const handleCancelClose = useCallback(() => {
    setConfirmingPositionId(null);
  }, []);

  // Handle confirm drag operation
  const handleConfirmDrag = useCallback(() => {
    // Calculate the final price from the dropped position
    if (pendingAction && pendingAction.newPosition) {
      const chartRect = overlayRef.current?.getBoundingClientRect();
      if (chartRect) {
        const relativeY = pendingAction.newPosition.y - chartRect.top;
        const finalPrice = calculatePriceFromY(relativeY);
        
        // Update the position with the calculated price
        const { type, positionId } = pendingAction;
        
        switch (type) {
          case 'move_position':
            // For position moves, we don't update the entry price immediately
            // Instead, we let the confirmed position handle the visual positioning
            // The entry price will be updated when the order is actually executed
            if (finalPrice) {
              // Store the new price for reference, but don't update the position yet
              // console.log('Position moved to new price:', finalPrice);
            }
            break;
          case 'update_sl':
            if (finalPrice) {
              setSLLevel(positionId, finalPrice);
            }
            break;
          case 'update_tp':
            if (finalPrice) {
              setTPLevel(positionId, finalPrice);
            }
            break;
        }
      }
    }
    
    // Set confirmed position to maintain the dropped location
    if (pendingAction && pendingAction.newPosition && pendingAction.startPosition) {
      const newPosition = pendingAction.newPosition;
      const startPosition = pendingAction.startPosition;
      
      // Calculate the delta from the current drag
      const currentDragDelta = newPosition.y - startPosition.y;
      
      // Get the previous confirmed position
      const previousConfirmedPos = pendingAction.type === 'move_position' ? positionConfirmedPos :
                                  pendingAction.type === 'update_sl' ? slConfirmedPos :
                                  pendingAction.type === 'update_tp' ? tpConfirmedPos : null;
      
      // Calculate cumulative delta from original position
      const previousDelta = previousConfirmedPos ? previousConfirmedPos.deltaY || 0 : 0;
      const cumulativeDelta = previousDelta + currentDragDelta;
      
      const confirmedPos = {
        y: newPosition.y,
        startY: startPosition.y, // Keep original startY for proper delta calculation
        deltaY: cumulativeDelta // Store the cumulative delta from original position
      };
      
      // Store the confirmed position for the specific element type
      // Each element type maintains its own independent position
      switch (pendingAction.type) {
        case 'move_position':
          setPositionConfirmedPos(confirmedPos);
          break;
        case 'update_sl':
          setSLConfirmedPos(confirmedPos);
          break;
        case 'update_tp':
          setTPConfirmedPos(confirmedPos);
          break;
      }
    }
    
    // Clear original data for the specific element type first
    switch (pendingAction?.type) {
      case 'move_position':
        setPositionOriginalData(null);
        break;
      case 'update_sl':
        setSLOriginalData(null);
        break;
      case 'update_tp':
        setTPOriginalData(null);
        break;
    }
    
    // Reset confirmation state last to ensure confirmed position is maintained
    handleConfirm();
    setConfirmingPositionId(null);
  }, [handleConfirm, pendingAction, calculatePriceFromY, overlayRef, updateChartPosition, setSLLevel, setTPLevel]);

  // Handle cancel drag operation
  const handleCancelDrag = useCallback(() => {
    // Restore to the position before this drag started
    const originalData = pendingAction?.type === 'move_position' ? positionOriginalData :
                        pendingAction?.type === 'update_sl' ? slOriginalData :
                        pendingAction?.type === 'update_tp' ? tpOriginalData : null;
    
    if (originalData) {
      if (originalData.entry) {
        updateChartPosition(position.id, { entry: originalData.entry });
      }
      if (originalData.slLevel) {
        setSLLevel(position.id, originalData.slLevel);
      }
      if (originalData.tpLevel) {
        setTPLevel(position.id, originalData.tpLevel);
      }
      
      // Restore the confirmed position for the specific element type
      if (originalData.confirmedPosition) {
        switch (pendingAction?.type) {
          case 'move_position':
            setPositionConfirmedPos(originalData.confirmedPosition);
            break;
          case 'update_sl':
            setSLConfirmedPos(originalData.confirmedPosition);
            break;
          case 'update_tp':
            setTPConfirmedPos(originalData.confirmedPosition);
            break;
        }
      } else {
        // No previous confirmed position, clear to return to original
        switch (pendingAction?.type) {
          case 'move_position':
            setPositionConfirmedPos(null);
            break;
          case 'update_sl':
            setSLConfirmedPos(null);
            break;
          case 'update_tp':
            setTPConfirmedPos(null);
            break;
        }
      }
    }
    
    // Reset drag state
    handleCancel();
    setConfirmingPositionId(null);
    setPositionOriginalData(null);
    setSLOriginalData(null);
    setTPOriginalData(null);
    
    // Reset drag initiation flags based on the action type
    if (pendingAction?.type === 'move_position') {
      setIsDragInitiated(false);
    } else if (pendingAction?.type === 'update_sl') {
      setIsDragInitiatedSL(false);
    } else if (pendingAction?.type === 'update_tp') {
      setIsDragInitiatedTP(false);
    }
  }, [positionOriginalData, slOriginalData, tpOriginalData, position.id, updateChartPosition, setSLLevel, setTPLevel, handleCancel, pendingAction]);

  // Get the execution price for positioning (not pixel position)
  const getExecutionPrice = useCallback(() => {
    try {
      // For executed orders, use the entry price directly
      if (!position.isPendingOrder) {
        const executionPrice = parseFloat(position.entry);
        // console.log('Execution Price Debug:', {
        //   positionId: position.id,
        //   entry: position.entry,
        //   executionPrice,
        //   currentPrice: currentPrice,
        //   isPendingOrder: position.isPendingOrder
        // });
        return executionPrice;
      }
      
      // For pending orders, use the entry price
      const executionPrice = parseFloat(position.entry);
      // console.log('Execution Price Debug (Pending):', {
      //   positionId: position.id,
      //   entry: position.entry,
      //   executionPrice,
      //   currentPrice: currentPrice,
      //   isPendingOrder: position.isPendingOrder
      // });
      return executionPrice;
    } catch (error) {
      console.warn('Error getting execution price:', error);
      return 0;
    }
  }, [position, currentPrice]);

  // Get the execution price for positioning
  const executionPrice = getExecutionPrice();
  
  // Calculate the Y position with safety checks and retry logic
  const [yPosition, setYPosition] = useState(0);
  
  // Use useEffect to calculate position after chart is ready
  useEffect(() => {
    const calculatePosition = () => {
      // Check if chart is ready before attempting calculation
      if (!chartRef.current || !chartRef.current.priceScale || typeof chartRef.current.priceScale !== 'function') {
        return;
      }
      
      if (executionPrice > 0) {
        const calculatedY = priceToY(executionPrice);
        if (!isNaN(calculatedY) && calculatedY > 0) {
          setYPosition(calculatedY);
        } else {
          // Retry with fewer attempts and longer delays to avoid spam
          const retryDelays = [500, 1000, 2000];
          let retryCount = 0;
          
          const retryCalculation = () => {
            if (retryCount < retryDelays.length) {
              setTimeout(() => {
                // Check if chart is ready before retrying
                if (chartRef.current && chartRef.current.priceScale && typeof chartRef.current.priceScale === 'function') {
                  const retryY = priceToY(executionPrice);
                  if (!isNaN(retryY) && retryY > 0) {
                    setYPosition(retryY);
                    return; // Success, stop retrying
                  }
                }
                retryCount++;
                retryCalculation();
              }, retryDelays[retryCount]);
            } else {
              // Final fallback - calculate based on current price vs execution price
              const currentPriceNum = parseFloat(currentPrice.toString().replace(/[,$]/g, ''));
              const priceDiff = Math.abs(currentPriceNum - executionPrice);
              const priceRange = Math.max(priceDiff * 2, 10); // At least 10 points range
              const minPrice = Math.min(currentPriceNum, executionPrice) - priceRange / 2;
              const maxPrice = Math.max(currentPriceNum, executionPrice) + priceRange / 2;
              
              const priceRatio = (maxPrice - executionPrice) / (maxPrice - minPrice);
              const fallbackY = priceRatio * 400; // Assume 400px chart height
              
              // console.log('Using fallback calculation:', {
              //   executionPrice,
              //   currentPriceNum,
              //   priceDiff,
              //   priceRange,
              //   minPrice,
              //   maxPrice,
              //   priceRatio,
              //   fallbackY
              // });
              
              setYPosition(fallbackY);
            }
          };
          
          retryCalculation();
        }
      }
    };
    
    calculatePosition();
  }, [executionPrice, priceToY, currentPrice]);
  
  // Debug the final positioning
  // console.log('Final Position Debug:', {
  //   positionId: position.id,
  //   executionPrice,
  //   yPosition,
  //   chartRefExists: !!chartRef.current,
  //   overlayRefExists: !!overlayRef.current
  // });

  // Individual drag styles for each wrap
  const getPositionDragStyles = () => {
    // If we're confirming a drag operation for position, maintain the dropped position
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id && pendingAction.type === 'move_position' && pendingAction.newPosition) {
      const startY = (pendingAction as any).startPosition?.y || 0;
      const deltaY = pendingAction.newPosition.y - startY;
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 0.9,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // During active dragging - add confirmed position offset if exists
    if (dragState.isDragging && dragState.draggedItemId === position.id && dragState.draggedItemType === 'position') {
      const currentDeltaY = dragState.currentPosition!.y - dragState.startPosition!.y;
      const confirmedOffset = positionConfirmedPos ? 
        positionConfirmedPos.deltaY || 0 : 0;
      const totalDeltaY = confirmedOffset + currentDeltaY;
      
      return {
        transform: `translateY(${totalDeltaY}px)`,
        opacity: 0.8,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // After confirmation, maintain the confirmed position
    if (positionConfirmedPos) {
      const deltaY = positionConfirmedPos.deltaY || (positionConfirmedPos.y - positionConfirmedPos.startY);
      // console.log('Position confirmed pos applied:', { deltaY, positionConfirmedPos });
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 1,
        zIndex: 25,
        transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
      };
    }

    return {
      transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
    };
  };

  const getSLDragStyles = () => {
    // If we're confirming a drag operation for SL, maintain the dropped position
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id && pendingAction.type === 'update_sl' && pendingAction.newPosition) {
      const startY = (pendingAction as any).startPosition?.y || 0;
      const deltaY = pendingAction.newPosition.y - startY;
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 0.9,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // During active dragging - add confirmed position offset if exists
    if (dragState.isDragging && dragState.draggedItemId === position.id && dragState.draggedItemType === 'sl') {
      const currentDeltaY = dragState.currentPosition!.y - dragState.startPosition!.y;
      const confirmedOffset = slConfirmedPos ? 
        slConfirmedPos.deltaY || 0 : 0;
      const totalDeltaY = confirmedOffset + currentDeltaY;
      
      return {
        transform: `translateY(${totalDeltaY}px)`,
        opacity: 0.8,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // After confirmation, maintain the confirmed position
    if (slConfirmedPos) {
      const deltaY = slConfirmedPos.deltaY || (slConfirmedPos.y - slConfirmedPos.startY);
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 1,
        zIndex: 25,
        transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
      };
    }
    
    // For pending orders with SL level set, calculate position based on chart coordinates
    if (position.isPendingOrder && position.slLevel && position.entry) {
      const entryPrice = parseFloat(position.entry);
      const slPrice = position.slLevel;
      
      // Calculate Y positions using chart conversion functions
      const entryY = priceToY(entryPrice);
      const slY = priceToY(slPrice);
      
      // Calculate the visual offset from entry position to SL position
      const visualOffset = slY - entryY;
      
      return {
        transform: `translateY(${visualOffset}px)`,
        opacity: 1,
        zIndex: 25,
        transition: 'transform 0.2s ease-out',
      };
    }

    return {
      transform: 'translateY(0%)', // Center position for SL
      transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
    };
  };

  const getTPDragStyles = () => {
    // If we're confirming a drag operation for TP, maintain the dropped position
    if (isConfirmingDrag && pendingAction && pendingAction.positionId === position.id && pendingAction.type === 'update_tp' && pendingAction.newPosition) {
      const startY = (pendingAction as any).startPosition?.y || 0;
      const deltaY = pendingAction.newPosition.y - startY;
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 0.9,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // During active dragging - add confirmed position offset if exists
    if (dragState.isDragging && dragState.draggedItemId === position.id && dragState.draggedItemType === 'tp') {
      const currentDeltaY = dragState.currentPosition!.y - dragState.startPosition!.y;
      const confirmedOffset = tpConfirmedPos ? 
        tpConfirmedPos.deltaY || 0 : 0;
      const totalDeltaY = confirmedOffset + currentDeltaY;
      
      return {
        transform: `translateY(${totalDeltaY}px)`,
        opacity: 0.8,
        zIndex: 1000,
        transition: 'none', // Disable transition during drag
      };
    }
    
    // After confirmation, maintain the confirmed position
    if (tpConfirmedPos) {
      const deltaY = tpConfirmedPos.deltaY || (tpConfirmedPos.y - tpConfirmedPos.startY);
      return {
        transform: `translateY(${deltaY}px)`,
        opacity: 1,
        zIndex: 25,
        transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
      };
    }
    
    // For pending orders with TP level set, calculate position based on chart coordinates
    if (position.isPendingOrder && position.tpLevel && position.entry) {
      const entryPrice = parseFloat(position.entry);
      const tpPrice = position.tpLevel;
      
      // Calculate Y positions using chart conversion functions
      const entryY = priceToY(entryPrice);
      const tpY = priceToY(tpPrice);
      
      // Calculate the visual offset from entry position to TP position
      const visualOffset = tpY - entryY;
      
      return {
        transform: `translateY(${visualOffset}px)`,
        opacity: 1,
        zIndex: 25,
        transition: 'transform 0.2s ease-out',
      };
    }

    return {
      transform: 'translateY(0%)', // Center position for TP
      transition: 'transform 0.2s ease-out', // Smooth transition when not dragging
    };
  };



  return (
    <div className="md:scale-100 scale-75 origin-top-right">
      {/* 1. Position Executed/Ordered Wrap - Always visible for all trade types */}
      <div
        key={`position-${position.isPendingOrder ? 'ordered' : 'executed'}-${position.id}-${index}`}
        className="position-executed-ordered-container absolute pointer-events-auto"
        style={{
          right: '230px',
          top: `${yPosition + 36}px`, // Position at actual execution price level
          zIndex: 25,
          ...getPositionDragStyles(),
        }}
        ref={positionRef}
        data-drag-initiated={isDragInitiated}
        data-drag-initiated-tp={isDragInitiatedTP}
        data-drag-initiated-sl={isDragInitiatedSL}
      >
        <div
          className={`
          p-0-1 p-0 py-1 rounded-sm border shadow-xs transition-all duration-200
            ${theme === 'dark'
              ? 'bg-gray-800/50 border-gray-800'
              : 'bg-white border-gray-100'
            }
            ${canDragWholePosition && position.isPendingOrder ? 'cursor-pointer' : ''}
          `}
          title=""
          onMouseDown={position.isPendingOrder ? handlePositionMouseDown : undefined}
        >
          <div className="flex items-center">
            {/* Cancel and Confirm Buttons - Show for close confirmation or any drag operations (position, SL, TP) */}
            {(isConfirmingClose || (isConfirmingDrag && pendingAction && (pendingAction.type === 'move_position' || pendingAction.type === 'update_sl' || pendingAction.type === 'update_tp'))) && (
              <>
                <button
                  onClick={isConfirmingClose ? handleCancelClose : handleCancelDrag}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200 mx-1 border ${!isDragInitiatedSL ? '' : '-mt-1'}
                    ${theme === 'dark' 
                      ? 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 border-gray-900/90' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                    }
                  `}
                  style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={isConfirmingClose ? handleConfirmClose : handleConfirmDrag}
                  className={`
                    px-2 py-1 text-xs font-medium rounded transition-colors duration-200 mx-1 border ${!isDragInitiatedSL || isDragInitiatedTP  ? 'me-2' : 'me-8 -mt-1'}
                    ${theme === 'dark' 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border-green-900/20' 
                      : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200'
                    }
                  `}
                  style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
                >
                  Confirm
                </button>
              </>
            )}

            {/* Buy/Sell Button - Only visible for limit or stop orders (pending orders) */}
            {position.isPendingOrder && (
              <div
                className={`
                  px-2 py-1 text-xs font-medium rounded transition-colors duration-200 mx-1 border cursor-pointer
                  ${isBuy 
                    ? 'bg-[#2AB7A6]/20 text-[#2AB7A6] border-[#2AB7A6]/30' 
                    : 'bg-[#ef4444]/20 text-[#ef4444] border-[#ef4444]/30'
                  } ${!isDragInitiatedSL || !isDragInitiatedTP ? 'me-16' : ''}
                `}
                style={{ padding: '2px 4px' }}
              >
                {isBuy ? 'Buy' : 'Sell'}
              </div>
            )}
              
            {/* Position Container */}
            <div
              className={`
                chart-position-container flex items-center justify-between rounded-sm transition-all duration-200 mx-1 border 
                ${theme === 'dark' 
                  ? `bg-gray-800 ${isBuy ? 'border-[#2AB7A6]' : 'border-[#ef4444]'}`
                  : 'bg-white'
                }
              `}
              style={{ minWidth: '60px', outline: 'none', boxShadow: 'none' }}
            >
                {/* Quantity Block */}
                <div
                  className={`
                    flex items-center py-1 text-xs font-normal rounded-l-sm border
                      ${isBuy 
                        ? 'bg-[#2AB7A6] text-white border-[#2AB7A6]' 
                        : 'bg-[#ef4444] text-white border-[#ef4444]'
                      }
                  `}
                  style={{ minWidth: '20px', padding: '0px 6px' }}
                >
                  {parseFloat(position.size).toFixed(2)}
                </div>
                
                {/* P&L Block */}
                <div
                  className={`
                    flex items-center justify-center px-1 py-0 text-xs font-medium
                    ${position.isPendingOrder 
                      ? (isBuy ? 'text-[#2AB7A6]' : 'text-[#ef4444]')
                      : (isProfit ? 'text-green-500' : 'text-red-500')
                    }
                  `}
                  style={{ minWidth: '55px' }}
                >
                  {position.isPendingOrder ? position.orderType?.toUpperCase() : position.pnl}
              </div>
              
              {/* Close Button */}
              <button
                onClick={handleCloseClick}
                disabled={isClosing}
                className={`
                  flex items-center justify-center w-6 h-4 rounded-r-sm transition-colors duration-200 border-l
                  ${isBuy ? 'border-l-[#2AB7A6]/30' : 'border-l-[#ef4444]/30'}
                  ${isClosing 
                    ? 'opacity-50 cursor-not-allowed' 
                    : theme === 'dark' 
                      ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }
                `}
                style={{ outline: 'none', boxShadow: 'none' }}
              >
                {isClosing ? (
                  <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
            
            {/* Order Type Indicator */}
            <div
              className={`
                px-1 py-0.5 text-xs rounded mx-1 cursor-pointer
                ${position.isAI 
                  ? 'bg-purple-500/20 text-purple-400' 
                  : 'bg-green-500/20 text-green-400'
                }
              `}
            >
              {position.isAI ? 'AI' : 'MI'}
            </div>
          </div>
        </div>

        {/* Horizontal Line - Brand Green Solid */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '50%',
            left: 'calc(100% - 1px)',
            right: '-0px',
            height: '1px',
            transform: 'translateY(-50%)',
            // background: `repeating-linear-gradient(to right, #ea580c 0px, #ea580c 3px, transparent 3px, transparent 6px)`,
            background: `${isBuy ? '#2AB7A6' : '#ef4444'}`,
            zIndex: 51,
            minWidth: '180px',
          }}
        />

        {/* Price Button - Brand Green Background */}
        <div
          className="absolute top-1/2 transform -translate-y-1/2 pointer-events-auto"
          style={{
            left: 'calc(100% + 165px)',
            zIndex: 52,
          }}
        >
          <div
            className={`
              p-1 py-0 text-xs rounded text-white shadow
              ${isBuy 
                ? 'bg-[#2AB7A6]/90 border border-green-600' 
                : 'bg-[#ef4444]/90 border border-red-600'
              }
            `}
            style={{ minWidth: '30px', maxWidth: '55px', textAlign: 'center', cursor: 'pointer' }}
          >
            {parseFloat(position.entry).toFixed(2)}
          </div>
        </div>
              </div>

      {/* 2. Take Profit Wrap - Hidden for AI trades */}
      {!position.isAI && (
        <div
          key={`take-profit-${position.id}-${index}`}
          className="takeprofit-container absolute pointer-events-auto"
          style={{
            right: '230px',
            top: `${yPosition + 36}px`, // Position at actual execution price level
            zIndex: 25,
            ...getTPDragStyles(),
          }}
          data-drag-initiated={isDragInitiated}
          data-drag-initiated-tp={isDragInitiatedTP}
          data-drag-initiated-sl={isDragInitiatedSL}
        >
        <div
          className={`
            p-0 py-1 rounded-sm border shadow-xs transition-all duration-200
            ${theme === 'dark'
              ? 'bg-gray-800/10 border-gray-800'
              : 'bg-white/10 border-gray-100'
            }
            ${canDragTP ? 'cursor-pointer' : ''}
          `}
          title=""
          onMouseDown={handleTPMouseDown}
        >
          <div className="flex items-center">
            {/* TP Button */}
            <button
              ref={tpRef}
              className={`
                px-2 py-1 text-xs font-medium rounded transition-colors duration-200 cursor-pointer mx-1 border border-dotted -mt-0
                ${theme === 'dark' 
                  ? 'bg-[#2AB7A6]/20 text-[#2AB7A6] hover:bg-[#2AB7A6]/30 border-[#2AB7A6]/30' 
                  : 'bg-[#2AB7A6]/10 text-[#2AB7A6] hover:bg-[#2AB7A6]/20 border-[#2AB7A6]/30'
                }
                ${canDragTP ? 'cursor-pointer' : ''}
              `}
              style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
              onMouseDown={e => {
                // Hide the button immediately on mousedown
                e.currentTarget.classList.remove('-mt-0');
                handleTPMouseDown(e);
              }}

            >
              TP
            </button>

            {/* Position Container */}
            <div className={`${isDragInitiatedTP ? 'opacity-100' : 'opacity-0'}`}>
              <div
                className={`
                  chart-position-container flex items-center justify-between rounded-sm transition-all duration-200 mx-1 border 
                  ${theme === 'dark' 
                    ? `bg-gray-800 ${isBuy ? 'border-[#2AB7A6]' : 'border-[#2AB7A6]'}`
                    : 'bg-white'
                  }
                `}
                style={{ minWidth: '60px', outline: 'none', boxShadow: 'none' }}
              >
                  {/* Quantity Block */}
                  <div
                    className={`
                    flex items-center py-1 text-xs font-normal rounded-l-sm border-0 border-r
                    text-[#2AB7A6]
                  `}
                  style={{ 
                    minWidth: '20px', 
                    padding: '0px 6px',
                    borderRight: '1px solid rgba(42, 183, 166, 0.3)'
                  }}
                  >
                    {parseFloat(position.size).toFixed(2)}
                  </div>
                  
                  {/* P&L Block */}
                  <div
                  className="flex items-center justify-center px-1 py-0 text-xs font-medium text-[#2AB7A6]"
                    style={{ minWidth: '55px' }}
                  >
                    {position.isPendingOrder ? position.orderType?.toUpperCase() : position.pnl}
                </div>
                
                {/* Close Button */}
                <button
                  onClick={handleCloseClick}
                  disabled={isClosing}
                  className={`
                    flex items-center justify-center w-6 h-4 rounded-r-sm transition-colors duration-200 border-l
                    border-l-[#2AB7A6]/30
                    ${isClosing 
                      ? 'opacity-50 cursor-not-allowed' 
                      : theme === 'dark' 
                        ? 'hover:bg-gray-700 text-[#2AB7A6] hover:text-[#2AB7A6]/80' 
                        : 'hover:bg-gray-100 text-[#2AB7A6] hover:text-[#2AB7A6]/80'
                    }
                  `}
                  style={{ outline: 'none', boxShadow: 'none' }}
                >
                  {isClosing ? (
                    <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {/* Order Type Indicator */}
            <div
              className={`
                px-1 py-0.5 text-xs rounded mx-1 cursor-pointer
                ${position.isAI 
                  ? 'bg-purple-500/20 text-purple-400' 
                  : 'bg-green-500/20 text-green-400'
                }
                ${isDragInitiatedTP ? 'opacity-100' : 'opacity-0'}
              `}
            >
              {position.isAI ? 'AI' : 'MI'}
            </div>
          </div>
        </div>

        {/* Brand Green Dashed Line */}
        <div
          className={`absolute pointer-events-none ${isDragInitiatedTP ? 'opacity-100' : 'opacity-0'}`}
          style={{
            top: '50%',
            left: 'calc(100% - 1px)',
            right: '-0px',
            height: '1px',
            transform: 'translateY(-50%)',
            background: `repeating-linear-gradient(to right, #2AB7A6 0px, #2AB7A6 3px, transparent 3px, transparent 6px)`,
            zIndex: 51,
            minWidth: '180px',
          }}
        />

        {/* TP Price Button - Brand Green Border, Gray Background */}
        <div
          className={`absolute top-1/2 transform -translate-y-1/2 pointer-events-auto ${isDragInitiatedTP ? 'opacity-100' : 'opacity-0'}`}
          style={{
            left: 'calc(100% + 165px)',
            zIndex: 52,
          }}
        >
          <div
            className="p-1 py-0 text-xs rounded-sm bg-gray-800 text-[#2AB7A6] border border-[#2AB7A6] shadow"
            style={{ minWidth: '30px', maxWidth: '55px', textAlign: 'center', cursor: 'pointer' }}
          >
            {position.tpLevel?.toFixed(2) || parseFloat(position.entry).toFixed(2)}
          </div>
        </div>
        </div>
      )}

      {/* 3. Stop Loss Wrap - Hidden for AI trades */}
      {!position.isAI && (
        <div
          key={`stop-loss-${position.id}-${index}`}
          className="stoploss-container absolute pointer-events-auto"
          style={{
            right: '233px',
            top: `${yPosition + 40}px`, // Position at actual execution price level
            zIndex: 25,
            ...getSLDragStyles(),
          }}
          data-drag-initiated={isDragInitiated}
          data-drag-initiated-tp={isDragInitiatedTP}
          data-drag-initiated-sl={isDragInitiatedSL}
        >
        <div
          className={`
            p-0 py-1 rounded-sm border shadow-xs transition-all duration-200
            ${theme === 'dark' 
              ? 'bg-gray-800/10 border-gray-800'
              : 'bg-white/10 border-gray-100'
            }
            ${canDragSL ? 'cursor-pointer' : ''}
          `}
          title=""
          onMouseDown={handleSLMouseDown}
        >
          <div className="flex items-center">
              {/* SL Button */}
              <button 
                ref={slRef}
                className={`
                  px-2 py-1 text-xs font-medium rounded transition-colors duration-200 cursor-pointer mx-1 border border-dashed ${!isDragInitiatedSL ? 'me-8 -mt-1' : 'me-2 -mt-0'}
                  ${theme === 'dark' 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border-red-900/20' 
                    : 'bg-red-50 text-red-600 hover:bg-red-100 border-red-200'
                  }
                  ${canDragSL ? 'cursor-pointer' : ''}
                `}
                style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
                onMouseDown={e => {
                  // Hide the button immediately on mousedown
                  // e.currentTarget.classList.add('me-1');
                  handleSLMouseDown(e);
                }}
              >
                SL
              </button>
              
              <div className={`${isDragInitiatedSL ? 'opacity-100' : 'opacity-0'}`}>
              {/* Position Container */}
              <div
                className={`
                  chart-position-container flex items-center justify-between rounded-sm transition-all duration-200 mx-1 border 
                  ${theme === 'dark' 
                    ? `bg-gray-800 ${isBuy ? 'border-orange-600' : 'border-orange-600'}`
                    : 'bg-white'
                  }
                `}
                style={{ minWidth: '60px', outline: 'none', boxShadow: 'none' }}
              >
                {/* Quantity Block */}
                <div
                  className={`
                  flex items-center py-1 text-xs font-normal rounded-l-sm border-0 border-r
                  text-orange-600
                `}
                style={{ 
                  minWidth: '20px', 
                  padding: '0px 6px',
                  borderRight: '1px solid rgba(234, 88, 12, 0.3)'
                }}
                >
                  {parseFloat(position.size).toFixed(2)}
                </div>
                  
                {/* P&L Block */}
                <div
                  className="flex items-center justify-center px-1 py-0 text-xs font-medium text-orange-600"
                    style={{ minWidth: '55px' }}
                  >
                    {position.isPendingOrder ? position.orderType?.toUpperCase() : position.pnl}
                </div>
                
                {/* Close Button */}
                <button
                  onClick={handleCloseClick}
                  disabled={isClosing}
                  className={`
                    flex items-center justify-center w-6 h-4 rounded-r-sm transition-colors duration-200 border-l
                    border-l-orange-600/30
                    ${isClosing 
                      ? 'opacity-50 cursor-not-allowed' 
                      : theme === 'dark' 
                        ? 'hover:bg-gray-700 text-orange-600 hover:text-orange-500' 
                        : 'hover:bg-gray-100 text-orange-600 hover:text-orange-700'
                    }
                  `}
                  style={{ outline: 'none', boxShadow: 'none' }}
                >
                  {isClosing ? (
                    <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            
              {/* Order Type Indicator */}
              <div 
                className={`
                  px-1 py-0.5 text-xs rounded mx-1 cursor-pointer
                  ${position.isAI 
                    ? 'bg-purple-500/20 text-purple-400' 
                    : 'bg-green-500/20 text-green-400'
                  }
                  ${isDragInitiatedSL ? 'opacity-100' : 'opacity-0'}
                `}
              >
                {position.isAI ? 'AI' : 'MI'}
              </div>
          </div>
        </div>

        {/* Orange Dashed Line */}
        <div
          className={`absolute pointer-events-none ${isDragInitiatedSL ? 'opacity-100' : 'opacity-0'}`}
          style={{
            top: '50%',
            left: 'calc(100% - 1px)',
            right: '-0px',
            height: '1px',
            transform: 'translateY(-50%)',
            background: `repeating-linear-gradient(to right, #ea580c 0px, #ea580c 3px, transparent 3px, transparent 6px)`,
            zIndex: 51,
            minWidth: '180px',
          }}
        />

        {/* SL Price Button - Orange Border, Gray Background */}
        <div
          className={`absolute top-1/2 transform -translate-y-1/2 pointer-events-auto ${isDragInitiatedSL ? 'opacity-100' : 'opacity-0'}`}
          style={{
            left: 'calc(100% + 165px)',
            zIndex: 52,
          }}
        >
          <div
            className="p-1 py-0 text-xs rounded-sm bg-gray-800 text-orange-600 border border-orange-600 shadow"
            style={{ minWidth: '30px', maxWidth: '55px', textAlign: 'center', cursor: 'pointer' }}
          >
            {position.slLevel?.toFixed(2) || parseFloat(position.entry).toFixed(2)}
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

// Export the component
export default DraggablePositionOverlay;